import { PortableText as PortableTextComponent } from '@portabletext/react';
import createImageUrlBuilder from '@sanity/image-url';
import { createClient } from 'next-sanity';

import { PageBlockVideo } from '@/components/blocks/PageBlockVideo';
import { Heading, List, Paragraph } from '@/components/common/MarkUp';
import { TooltipV2 } from '@/components/common/TooltipV2';
import {
  determineDestination,
  VisuallyHidden,
} from '@/components/common/utils';

import type { SanityImageSource } from '@sanity/image-url/lib/types/types';
import type { SanityClient } from 'next-sanity';

// TODO: can be moved into a seperate file (e.g. sanity.env.ts)

export const readToken = process.env.SANITY_API_READ_TOKEN || '';
export const dataset = process.env.STORYBOOK
  ? 'staging'
  : process.env.NEXT_PUBLIC_SANITY_DATASET || 'development';
export const projectId = process.env.STORYBOOK
  ? 'dlb8d5f6'
  : process.env.NEXT_PUBLIC_SANITY_PROJECT_ID;
export const apiVersion =
  process.env.NEXT_PUBLIC_SANITY_API_VERSION || '2023-06-21';

/**
 * Set up a helper function for generating Image URLs with only the asset reference data in your documents.
 * Read more: https://www.sanity.io/docs/image-url
 * */
export const getSanityImage = (source: SanityImageSource) =>
  createImageUrlBuilder({ projectId, dataset }).image(source);

export const getSanityImageUrl = (source: SanityImageSource) => {
  const image = getSanityImage(source);

  try {
    return image.url();
  } catch (error) {
    console.warn(error);
    return '';
  }
};

export const components = {
  block: {
    normal: ({ children }) => <Paragraph size="body-md">{children}</Paragraph>,
    heading: ({ children }) => <Heading size="subheadline">{children}</Heading>,
    youtube: ({ value }) => (
      <PageBlockVideo url={value.url} caption={value.caption} />
    ),
  },
  marks: {
    strong: ({ children }) => <b>{children}</b>,
    link: ({ value, children }) => {
      const { href } = value;

      const destination = determineDestination(href);
      return (
        <a
          href={href}
          target={destination.target}
          rel={destination.isExternalLink ? 'external noopener' : null}
        >
          {children}
          {destination.isExternalLink && (
            <VisuallyHidden>{destination.newTabText}</VisuallyHidden>
          )}
        </a>
      );
    },
    tooltip: ({ children, value }) => {
      if (!value.content) return <span>{children}</span>;

      return (
        <TooltipV2 key={value._key} mark={value}>
          {children}
        </TooltipV2>
      );
    },
  },
  list: ({ value, children }) => {
    if (value.listItem === 'number') {
      return (
        <List type="ol" size="body-md">
          {children}
        </List>
      );
    } else {
      return <List size="body-md">{children}</List>;
    }
  },
  listItem: ({ children }) => (
    <li>
      <span>{children}</span>
    </li>
  ),
};

// componentsV2 holds the styling of the redesign
export const componentsV2 = {
  block: {
    normal: ({ children }) => <Paragraph size="body-md">{children}</Paragraph>,
    heading: ({ children }) => (
      <Heading size="subheadline-bold">{children}</Heading>
    ),
    'body-small': ({ children }) => (
      <Paragraph size="body-sm">{children}</Paragraph>
    ),
    'headline-sm': ({ children }) => (
      <Heading size="body-md-semi-bold">{children}</Heading>
    ),
  },
};

export const PortableText = (props) => (
  <PortableTextComponent
    onMissingComponent={(message, options) => {
      // eslint-disable-next-line no-console
      console.error('PortableText: missing component', { message, options });
    }}
    components={{ ...components }}
    {...props}
  />
);

export const PortableTextV2 = (props) => (
  <PortableTextComponent
    onMissingComponent={(message, options) => {
      // eslint-disable-next-line no-console
      console.error('PortableTextV2: missing component', { message, options });
    }}
    components={{ ...components, ...componentsV2 }}
    {...props}
  />
);

interface ClientOptions {
  useCdn?: boolean;
}

export function getClient(
  preview?: { token: string },
  options?: ClientOptions,
): SanityClient {
  const { useCdn = false } = options ?? {};

  const client = createClient({
    projectId,
    dataset,
    apiVersion,
    useCdn,
    perspective: 'published',
  });

  if (preview) {
    if (!preview.token) {
      throw new Error('You must provide a token to preview drafts');
    }
    return client.withConfig({
      token: preview.token,
      useCdn: false,
      ignoreBrowserTokenWarning: true,
      perspective: 'previewDrafts',
    });
  }
  return client;
}

export const blocksToText = (blocks, opts = {}) => {
  const options = { nonTextBehavior: 'remove', ...opts };

  if (!blocks) {
    return '';
  }

  return blocks
    .map((block) => {
      if (block._type !== 'block' || !block.children) {
        return options.nonTextBehavior === 'remove'
          ? ''
          : `[${block._type} block]`;
      }

      return block.children.map((child) => child.text).join('');
    })
    .join('\n\n');
};
